import { useDispatch } from 'react-redux'
import { ConnectorModalType } from '@typesApp/Connector'
import { useEnvironment } from '@hooks/useEnvironment'
import { showConnectorModal } from '@redux/slices/connectorSlice'
import { useNullifyUpdateCart } from './useNullifyUpdateCart'
import { useInterceptPrompt } from './useInterceptPrompt'

interface UseCartInterceptReturn {
  interceptAddCartItem: (originalMethod: UpdateCartCallback) => void
  interceptRemoveCartItem: (originalMethod: UpdateCartCallback) => void
  interceptChangeQuantity: (originalMethod: UpdateCartCallback) => void
  interceptEditCartItem: (originalMethod: UpdateCartCallback) => void
  interceptApplyPromoCode: (originalMethod: UpdateCartCallback) => void
  interceptRemovePromoCode: (originalMethod: UpdateCartCallback) => void
  interceptApplySubscription: (originalMethod: UpdateCartCallback) => void
}

type UpdateCartCallback = () => void

interface UpdateCartOptions {
  modalType: ConnectorModalType
  callback: UpdateCartCallback
}

const DISABLE_PROMPT = {
  lcr: true,
  cle: true,
} as const

/**
 * Intercept cart change events if a claim has been submitted and accepted
 *
 * FIXME: once cart change prompts are disabled for all brands and F/E integration
 * points are removed we can remove the cart intercpet components entirely.
 */
export const useCartIntercept = (): UseCartInterceptReturn => {
  const dispatch = useDispatch()
  const { brand } = useEnvironment()
  const shouldPrompt = useInterceptPrompt()
  const nullifyUpdateCart = useNullifyUpdateCart()

  const disablePrompt = DISABLE_PROMPT[brand] === true

  /**
   * Add cart item - no need to update CTA since add to cart is not triggered from cart page
   */
  const interceptAddCartItem = (originalMethod: UpdateCartCallback) => {
    handleUpdateCart({
      callback: originalMethod,
      modalType: ConnectorModalType.ADD_ITEM,
    })
  }

  const handleUpdateCart = (options: UpdateCartOptions) => {
    if (disablePrompt) {
      if (typeof options?.callback === 'function') {
        options.callback()
      }
      return
    }

    // on confirm, nullify claim and reset cart, then invoke original cart update method
    const confirmCallback = () => {
      nullifyUpdateCart({ callback: options.callback })
    }

    const showModal = options.modalType && options.modalType !== ConnectorModalType.NONE
    if (showModal && shouldPrompt()) {
      dispatch(showConnectorModal({ type: options.modalType, confirmCallback }))
    } else {
      options.callback()
    }
  }

  /**
   * Remove cart item - trigger CTA update and check eligible products
   */
  const interceptRemoveCartItem = (originalMethod: UpdateCartCallback) => {
    handleUpdateCart({
      callback: originalMethod,
      modalType: ConnectorModalType.REMOVE_ITEM,
    })
  }

  /**
   * Change CL quantity - trigger CTA update
   */
  const interceptChangeQuantity = (originalMethod: UpdateCartCallback) => {
    handleUpdateCart({
      callback: originalMethod,
      modalType: ConnectorModalType.CHANGE_QUANTITY,
    })
  }

  /**
   * Edit cart item (edit RX) - no need to update CTA as edit will navigate away from checkout page
   */
  const interceptEditCartItem = (originalMethod: UpdateCartCallback) => {
    handleUpdateCart({
      callback: originalMethod,
      // always allow the edit to proceed and update cart which will
      // automatically nullify cart on B/E
      modalType: ConnectorModalType.NONE,
    })
  }

  const interceptApplyPromoCode = (originalMethod: UpdateCartCallback) => {
    handleUpdateCart({
      callback: originalMethod,
      modalType: ConnectorModalType.APPLY_PROMO_CODE,
    })
  }

  const interceptRemovePromoCode = (originalMethod: UpdateCartCallback) => {
    handleUpdateCart({
      callback: originalMethod,
      modalType: ConnectorModalType.REMOVE_PROMO_CODE,
    })
  }

  const interceptApplySubscription = (originalMethod: UpdateCartCallback) => {
    handleUpdateCart({
      callback: originalMethod,
      modalType: ConnectorModalType.APPLY_SUBSCRIPTION,
    })
  }

  return {
    interceptAddCartItem,
    interceptRemoveCartItem,
    interceptChangeQuantity,
    interceptEditCartItem,
    interceptApplyPromoCode,
    interceptRemovePromoCode,
    interceptApplySubscription,
  }
}
